import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants.js';
import { parseISO, subWeeks, subDays, addDays, startOfDay } from 'date-fns';
import {
  filterObjectByKeys,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  isNumeric,
} from '../community-hats-js-library/utils/generalFunctions.js';
import { getTicks } from '../utils/chartFunctions.js';
import { formatInStudyLocationToCustom, getDaysBetweenDates } from '../community-hats-js-library/utils/dateFunctions.js';

export const computeWearableCoverage = (emails) => {
  const coverages = [
    libCon.ATF_WEARABLE_MORNING_COVERAGE,
    libCon.ATF_WEARABLE_AFTERNOON_COVERAGE,
    libCon.ATF_WEARABLE_EVENING_COVERAGE,
    libCon.ATF_WEARABLE_NIGHT_COVERAGE,
    libCon.ATF_WEARABLE_TOTAL_COVERAGE,
  ];

  // Generates the long format
  let arrayRecords = Object.fromEntries([libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS].map((key) => [key, []]));

  for (const em of Object.values(emails)) {
    if (
      em[libCon.ATF_TYPE] === libCon.ATF_PARTICIPANT &&
      !isNullOrUndefined(em[libCon.ATF_ACTIVE]) &&
      em[libCon.ATF_ACTIVE] === libCon.YES &&
      getDaysBetweenDates(em[libCon.ATF_LATEST_PLACEMENT_START_DATE], new Date()) > 20 // More than 20 days have to had passed for it to count
    ) {
      let area = !isNullOrUndefinedOrEmpty(em[libCon.ATF_AREA]) ? em[libCon.ATF_AREA][0] : null;

      if (!isNullOrUndefined(area) && area in arrayRecords) {
        let d = Object.fromEntries(coverages.map((cov) => [cov, cov in em ? em[cov] : 0]));
        arrayRecords[libCon.ATF_ALL_LOCATIONS].push(d);
        arrayRecords[area].push(d);
      }
    }
  }

  // Reduces
  const averageCoverageByLocation = {};

  for (const [location, records] of Object.entries(arrayRecords)) {
    const count = records.length;

    const totals = records.reduce((acc, record) => {
      for (const key of coverages) {
        acc[key] = (acc[key] || 0) + (record[key] || 0);
      }
      return acc;
    }, {});

    const averages = Object.fromEntries(
      coverages.map((key) => [key, count > 0 ? Math.round((100 * totals[key]) / count) : 0])
    );

    averageCoverageByLocation[location] = averages;
  }

  return averageCoverageByLocation;
};

export const computeVisitedHouses = (receivedFiles) => {
  const startDate = startOfDay(subWeeks(new Date(), 3));

  let streamLines = {};

  // Iterates to construct the dictionary
  for (const k of Object.keys(receivedFiles)) {
    let file = receivedFiles[k];

    if (
      !isNullOrUndefined(file[libCon.ATF_DATE_UPLOADED]) &&
      !isNullOrUndefinedOrEmpty(file[libCon.ATF_ACTIVE_LOCATION]) &&
      !isNullOrUndefinedOrEmpty(file[libCon.ATF_AREA])
    ) {
      let location = file[libCon.ATF_ACTIVE_LOCATION][0];
      let area = file[libCon.ATF_AREA][0];
      let d = formatInStudyLocationToCustom(file[libCon.ATF_DATE_UPLOADED], 'yyyy-MM-dd');

      if (parseISO(d) < startDate) continue;

      if (!(d in streamLines)) {
        streamLines[d] = Object.fromEntries(libCon.AREAS.map((key) => [key, new Set()]));
      }

      streamLines[d][area].add(location);
    }
  }

  // Transforms into an array
  let chartData = Object.keys(streamLines).map((k) => {
    let ob = {
      [locCon.DATE]: new Date(k).getTime(),
    };

    for (const area of Object.keys(streamLines[k])) {
      ob[area] = streamLines[k][area].size;
    }

    return ob;
  });

  chartData.sort((a, b) => a[locCon.DATE] - b[locCon.DATE]);

  let response = {
    [locCon.CHART_DATA]: chartData,
    [locCon.TICKS]: getTicks(subDays(startDate, 1), addDays(startOfDay(new Date()), 1), 10),
    [locCon.DOMAIN]: [subDays(startDate, 1), addDays(startOfDay(new Date()), 1)],
  };

  return response;
};

export const computeStatusTable = (participants) => {
  const acceptedStatus = [
    libCon.DEVICE_STATUS_OK,
    libCon.DEVICE_STATUS_WAITING_COLLECTION,
    libCon.DEVICE_STATUS_WAITING_COLLECTION_LOOSING_DATA,
    libCon.DEVICE_STATUS_ERROR_IN_COLLECTION,
  ];

  let tableSumaries = Object.fromEntries(
    [libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS].map((key) => [key, { [libCon.ATF_LOCATION]: key }])
  );

  for (const p of Object.values(participants)) {
    if (
      !isNullOrUndefinedOrEmpty(p[libCon.ATF_AREA]) &&
      p[libCon.ATF_AREA] in tableSumaries &&
      libCon.AT_COLLECTION_STATUS in p
    ) {
      let area = p[libCon.ATF_AREA];
      let status = p[libCon.AT_COLLECTION_STATUS];
      if (!acceptedStatus.includes(status)) status = libCon.ATF_OTHER;

      tableSumaries[area][status] = (tableSumaries[area][status] || 0) + 1;
      tableSumaries[libCon.ATF_ALL_LOCATIONS][status] = (tableSumaries[libCon.ATF_ALL_LOCATIONS][status] || 0) + 1;
    }
  }

  return Object.values(tableSumaries);
};

export const computeLossingDataRecords = (participants) => {
  let response = Object.fromEntries(
    [libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS].map((key) => [
      key,
      {
        [locCon.MORE_30]: [],
        [locCon.MORE_20]: [],
        [locCon.MORE_10]: [],
        [locCon.MORE_0]: [],
      },
    ])
  );

  for (const p of Object.values(participants)) {
    if (
      !isNullOrUndefinedOrEmpty(p[libCon.ATF_AREA]) &&
      p[libCon.ATF_AREA] in response &&
      libCon.ATF_DAYS_UNTIL_EXPIRE in p
    ) {
      let area = p[libCon.ATF_AREA];
      let days = p[libCon.ATF_DAYS_UNTIL_EXPIRE];
      let partInfo = filterObjectByKeys(p, [libCon.ATF_NAME, libCon.ATF_SEWA_ID, libCon.ATF_DAYS_UNTIL_EXPIRE]);

      if (days < -30) {
        response[area][locCon.MORE_30].push(partInfo);
        response[libCon.ATF_ALL_LOCATIONS][locCon.MORE_30].push(partInfo);
      } else if (days < -20) {
        response[area][locCon.MORE_20].push(partInfo);
        response[libCon.ATF_ALL_LOCATIONS][locCon.MORE_20].push(partInfo);
      } else if (days < -10) {
        response[area][locCon.MORE_10].push(partInfo);
        response[libCon.ATF_ALL_LOCATIONS][locCon.MORE_10].push(partInfo);
      } else if (days < 0) {
        response[area][locCon.MORE_0].push(partInfo);
        response[libCon.ATF_ALL_LOCATIONS][locCon.MORE_0].push(partInfo);
      }
    }
  }

  // Sorts
  for (const k of Object.keys(response)) {
    for (const d of Object.keys(response[k])) {
      response[k][d].sort((a, b) => a[libCon.ATF_DAYS_UNTIL_EXPIRE] - b[libCon.ATF_DAYS_UNTIL_EXPIRE]);
    }
  }

  return response;
};

export const computeCollectionDurationRecords = (participants) => {
  let arrayRecords = Object.fromEntries([libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS].map((key) => [key, []]));

  for (const p of Object.values(participants)) {
    if (
      !isNullOrUndefinedOrEmpty(p[libCon.ATF_AREA]) &&
      p[libCon.ATF_AREA] in arrayRecords &&
      libCon.ATF_AVERAGE_COLLECTION_DURATION in p
    ) {
      let area = p[libCon.ATF_AREA];
      let duration = p[libCon.ATF_AVERAGE_COLLECTION_DURATION];
      if (isNumeric(duration)) {
        duration /= 60; // Converts to minutes
        arrayRecords[area].push(duration);
        arrayRecords[libCon.ATF_ALL_LOCATIONS].push(duration);
      }
    }
  }

  return arrayRecords;
};

export const computeWearableCoverageStreams = (coverage) => {
  const startDate = startOfDay(subDays(new Date(), 90));
  const endDate = startOfDay(new Date());

  const values = coverage.values;

  // Get the length of arrays to determine how many days of data we have
  const allLocationsData = values[libCon.ATF_ALL_LOCATIONS];
  const allPeriodArray = allLocationsData[libCon.ALL];
  const totalDays = allPeriodArray.length;

  // Get the last 90 days (or all available if less than 90)
  const daysToInclude = Math.min(90, totalDays);

  // Helper function to get last N elements from an array
  const getLastN = (arr, n) => arr.slice(-n);

  // Get data for All Locations view (All period for each area)
  const allLocationsAllPeriod = getLastN(values[libCon.ATF_ALL_LOCATIONS][libCon.ALL], daysToInclude);

  // Build ATF_ALL_LOCATIONS chart data - dynamically include all areas from libCon.AREAS
  const allLocationsChartData = [];
  for (let i = 0; i < daysToInclude; i++) {
    const date = addDays(startDate, i);
    const dayData = {
      [locCon.DATE]: date.getTime(),
      [libCon.ATF_ALL_LOCATIONS]: allLocationsAllPeriod[i],
    };
    // Add each area's "All" period value dynamically
    for (const area of libCon.AREAS) {
      const areaAllPeriod = getLastN(values[area][libCon.ALL], daysToInclude);
      dayData[area] = areaAllPeriod[i];
    }
    allLocationsChartData.push(dayData);
  }

  // Helper function to build location-specific chart data
  const buildLocationChartData = (locationKey) => {
    const locationData = values[locationKey];
    const morning = getLastN(locationData[libCon.MORNING], daysToInclude);
    const afternoon = getLastN(locationData[libCon.AFTERNOON], daysToInclude);
    const evening = getLastN(locationData[libCon.EVENING], daysToInclude);
    const night = getLastN(locationData[libCon.NIGHT], daysToInclude);
    const all = getLastN(locationData[libCon.ALL], daysToInclude);

    const chartData = [];
    for (let i = 0; i < daysToInclude; i++) {
      const date = addDays(startDate, i);
      chartData.push({
        [locCon.DATE]: date.getTime(),
        [libCon.MORNING]: morning[i],
        [libCon.AFTERNOON]: afternoon[i],
        [libCon.EVENING]: evening[i],
        [libCon.NIGHT]: night[i],
        [libCon.ALL]: all[i],
      });
    }
    return chartData;
  };

  // Build chart data dynamically for all areas in libCon.AREAS
  let chartData = {
    [libCon.ATF_ALL_LOCATIONS]: allLocationsChartData,
  };

  for (const area of libCon.AREAS) {
    chartData[area] = buildLocationChartData(area);
  }

  let response = {
    [locCon.DOMAIN]: [startDate, addDays(endDate, 1)],
    [locCon.TICKS]: getTicks(startDate, addDays(endDate, 1), 10),
    [locCon.CHART_DATA]: chartData,
  };

  return response;
};
